import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { IFilterOption } from "@faire/web-api/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api/indigofair/locale/ILocaleKey";
import { getIsoToFromCountryNamesSorted } from "@faire/web/common/consts/ISO_TO_FROM_COUNTRY_NAME";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { FromCountry } from "@faire/web/common/localization/placeholderTaxonomy";
import capitalize from "lodash/capitalize";

import { filterNonCategoryNodesCategoryFilterOptions } from "@faire/retailer/views/brand/logged-out/shared/metaTagText";

export function getBrandAboutSubheader(
  brand: IBrandViewV2Response.ILeanBrand,
  filterSections: IFilterSection[],
  localeKey: ILocaleKey | undefined,
  strictLocalize: StrictLocalizeFunction
): string | null {
  const categoryFilterOptions =
    filterSections.find(
      ({ field_name }) => field_name === IFilterSection.FieldName.CATEGORY
    )?.options ?? null;

  if (!categoryFilterOptions?.length) {
    return null;
  }

  const filteredCategoryFilterOptions =
    filterNonCategoryNodesCategoryFilterOptions(categoryFilterOptions);

  if (filteredCategoryFilterOptions.length >= 3) {
    return strictLocalize(
      {
        defaultMessage:
          "They carry a selection of wholesale {firstCategory} products, {secondCategory} products, {thirdCategory} products, and more for your store.",
        description: {
          text: "Gives an overview of the brand and the kinds of products they sell (used for search-engine optimization purposes to add more visibility on brand + category related searches).",
          img: "https://cdn.faire.com/fastly/db561700e3a68d795c50ceccec55742bbfd4ea652023e242fde03c7ca415cd2f.png",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        firstCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[0],
          localeKey
        ),
        secondCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[1],
          localeKey
        ),
        thirdCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[2],
          localeKey
        ),
      })
    );
  } else if (filteredCategoryFilterOptions.length >= 1) {
    const fromCountryNames = getIsoToFromCountryNamesSorted(strictLocalize);
    const fromCountry: FromCountry | undefined =
      fromCountryNames[brand.based_in as keyof typeof fromCountryNames];

    if (fromCountry) {
      return strictLocalize(
        {
          defaultMessage:
            "They specialize in wholesale {firstCategory} products and are {FromCountry}.",
          description: {
            text: "Gives an overview of the brand based on the primary category they sell as well as which country they're based in (used for search-engine optimization purposes to add more visibility on the brand).",
            img: "https://cdn.faire.com/fastly/e0ee686fd81a265fa4d7cc4c4e09095d70ab9aa4cbd75b81c7d6baab6b56ece3.png",
          },
        },
        DEPRECATED_backwardsCompatibleValues({
          firstCategory: getCategoryDisplayName(
            filteredCategoryFilterOptions[0],
            localeKey
          ),
          FromCountry: fromCountry,
        })
      );
    }
  }

  return null;
}

const getCategoryDisplayName = (
  categoryFilter: IFilterOption | undefined,
  localeKey: ILocaleKey | undefined
): string => {
  const categoryDisplayName =
    categoryFilter?.display_name ?? categoryFilter?.global_display_name ?? "";

  const language = (localeKey?.language ??
    Language.LANGUAGE_UNKNOWN) as Language;
  if (capitalizeWhiteList.has(language)) {
    return capitalize(categoryDisplayName);
  }

  return categoryDisplayName.toLowerCase();
};

const capitalizeWhiteList: Set<Language> = new Set([
  Language.FRA,
  Language.DEU,
  Language.ITA,
  Language.SPA,
]);

// TODO: (laszlo) we cannot import `@faire/web/common/localize` in RSC because it has a "use client" directive.
const DEPRECATED_backwardsCompatibleValues = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): T => values as any;
